import { useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { UpsellLandingPagesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { LandingPageBlocks } from 'modules/discover/components/LandingPageBlocks'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

const UPSELL_ORIGIN_STORAGE_KEY = 'upsell_origin'

const LandingPage = () => {
  const {
    access: { locale },
  } = useCurrentSession()
  const location = useLocation()

  const datoLocale = getDatoLocale(locale)
  const { id } = useParams()
  const preview = new URLSearchParams(location.search).get('preview') === 'true'

  const { data: landingPageData, loading } = useQuery(UpsellLandingPagesDocument, {
    variables: { locale: datoLocale, slug: id || '', preview },
  })

  const landingPage = landingPageData?.upsell.landingPagesConnection.edges[0]?.node
  const [currentUpsellOrigin, setUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>(UPSELL_ORIGIN_STORAGE_KEY, 5)
  const setUpsellOriginRef = useRef(setUpsellOrigin)
  setUpsellOriginRef.current = setUpsellOrigin

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const upsellOriginParam = queryParams.get('upsell_origin')

    if (upsellOriginParam) {
      let upsellOriginData: {
        request_source: string
        request_source_category?: string
        request_source_component?: string
      } | null = null

      try {
        const parsed = JSON.parse(decodeURIComponent(upsellOriginParam))
        if (parsed && parsed.request_source) {
          upsellOriginData = {
            request_source: parsed.request_source,
            request_source_category: parsed.request_source_category,
            request_source_component: parsed.request_source_component,
          }
        }
      } catch {
        upsellOriginData = { request_source: upsellOriginParam }
      }

      if (upsellOriginData) {
        const hasChanged =
          currentUpsellOrigin?.request_source !== upsellOriginData.request_source ||
          currentUpsellOrigin?.request_source_category !==
            upsellOriginData.request_source_category ||
          currentUpsellOrigin?.request_source_component !==
            upsellOriginData.request_source_component

        if (hasChanged) {
          setUpsellOriginRef.current(upsellOriginData)
        }
      }
    }
  }, [
    location.search,
    currentUpsellOrigin?.request_source,
    currentUpsellOrigin?.request_source_category,
    currentUpsellOrigin?.request_source_component,
  ])

  useEffect(() => {
    if (landingPage) {
      track(trackingEvents.discover.seeLandingPage, {
        slug: landingPage.slug,
      })
    }
  }, [landingPage])

  if (loading) {
    return <Spinner />
  }
  return (
    <SelfUpsellProvider
      availableAddons={landingPage?.moduleRelated?.billingAddonReference || []}
      availableCrossSellingAddons={landingPage?.moduleRelated?.billingAddonReference || []}
      availableMandatoryCrossSellingAddons={landingPage?.moduleRelated?.billingAddonReference || []}
      moduleName={landingPage?.moduleRelated?.chargebeeModuleName || ''}
    >
      <ResponsivePage.Body variant="fullscreen" authorized={!!landingPage}>
        <Box
          gap="s80"
          style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}
          padding="s32"
        >
          {landingPage?.slug && (
            <LandingPageBlocks
              blocks={landingPage?.blocks}
              slug={landingPage.slug}
              landingPage={landingPage}
              currentUpsellOrigin={currentUpsellOrigin}
            />
          )}
        </Box>
      </ResponsivePage.Body>
    </SelfUpsellProvider>
  )
}

export default LandingPage
